/**
 * GENERATED_ASSET — reed curtain ported from the batch001 static kit (`static-kit/source/reeds.ts`).
 * Art is unchanged; only layout was expanded for readability. The protected gap is visual only — it creates no
 * trigger and no collision hole. Edge reeds are a separable foreground layer and never cover the protected gap.
 */
import type { DraftArtifact, Point2, ReedCurtainRecipe } from './contracts';
import { Construction, anchor, el, n } from './construction';

export interface ReedStalkGeometry {
  readonly side: number;
  readonly index: number;
  readonly controlHull: Point2[];
  readonly root: Point2;
  readonly tip: Point2;
}

export function buildReedCurtain(c: Construction): DraftArtifact {
  const r = c.recipe as ReedCurtainRecipe;
  const p = r.params;
  const w = p.width;
  const h = p.height;
  const gap = p.gapWidth;
  const tieY = -h * p.tieHeightRatio;
  const tieOffset = gap / 2 + (w - gap) * (p.opening === 'gathered_open' ? 0.30 : 0.22);
  let back = '';
  let middle = '';
  let front = '';
  let ties = '';
  const stalks: ReedStalkGeometry[] = [];

  for (const side of [-1, 1]) {
    const tx = side * tieOffset;
    for (let i = 0; i < p.stalksPerSide; i++) {
      const id = `reed.${side < 0 ? 'left' : 'right'}.${i}`;
      const thick = 3.4 + c.random(`${id}.width`) * 2;
      const root = side * (gap / 2 + 5 + i / (p.stalksPerSide - 1) * ((w - gap) / 2 - 10)
        + (i === 0 ? c.random(`${id}.root`) * 1.5 : (c.random(`${id}.root`) - 0.5) * 3));
      const bend = p.opening === 'gathered_open' ? tx : root * 0.74 + tx * 0.26 + (c.random(`${id}.bend`) - 0.5) * 4;
      const tip = side * Math.max(gap / 2 + 5, Math.abs(root) + (c.random(`${id}.spread`) - 0.5) * p.tipSpread);
      const tipY = -h * (0.74 + c.random(`${id}.height`) * 0.26);
      const lower = side * Math.max(gap / 2 + 4, Math.abs((root + bend) / 2 + side * 3 + (c.random(`${id}.lean`) - 0.5) * 6));
      const upper = (bend + tip) / 2 + side * 5;
      const pts = [
        { x: root - thick / 2, y: 0 }, { x: lower - thick / 2, y: tieY * 0.5 }, { x: bend - thick * 0.3, y: tieY },
        { x: upper - thick * 0.2, y: (tieY + tipY) / 2 }, { x: tip, y: tipY },
        { x: upper + thick * 0.2, y: (tieY + tipY) / 2 }, { x: bend + thick * 0.3, y: tieY },
        { x: lower + thick / 2, y: tieY * 0.5 }, { x: root + thick / 2, y: 0 },
      ];
      const d = `M${n(pts[0].x)} 0Q${n(pts[1].x)} ${n(pts[1].y)} ${n(pts[2].x)} ${n(tieY)}`
        + `Q${n(pts[3].x)} ${n(pts[3].y)} ${n(tip)} ${n(tipY)}`
        + `Q${n(pts[5].x)} ${n(pts[5].y)} ${n(pts[6].x)} ${n(tieY)}`
        + `Q${n(pts[7].x)} ${n(pts[7].y)} ${n(pts[8].x)} 0Z`;
      const fill = c.color(i % 3 === 0 ? 'reedLight' : i % 3 === 1 ? 'reedBase' : 'reedShadow');
      let stalk = c.path(d, pts, fill, c.color('reedShadow'), 0.35, { 'data-stalk': id });
      if (r.detail !== 'readable' && i % 3 === 0) {
        stalk += c.path(`M${n(tip)} ${n(tipY + 9)}l${side * 6} -7`, [{ x: tip, y: tipY + 9 }, { x: tip + side * 6, y: tipY + 2 }],
          'none', c.color('reedLight'), 1.1, {}, false);
      }
      if (r.detail !== 'readable' && i % 4 === 1) {
        stalk += c.ellipse(tip, tipY + 3, 1.65, 5, c.color('reedShadow'), { 'data-part': 'reed-seed-head' }, false);
      }
      // Edge reeds are separable from the back layer; never cover the protected gap.
      if (i === 0) front += stalk;
      else if (i % 3 === 2) back += stalk;
      else middle += stalk;
      stalks.push({ side, index: i, controlHull: pts, root: { x: root, y: 0 }, tip: { x: tip, y: tipY } });
    }
    if (p.opening === 'concealed') {
      ties += c.path(`M${n(side * (gap / 2 + 6))} ${n(tieY)}Q${n(tx)} ${n(tieY + 5)} ${n(side * (w / 2 - 5))} ${n(tieY - 1)}`,
        [{ x: side * (gap / 2 + 6), y: tieY }, { x: tx, y: tieY + 5 }, { x: side * (w / 2 - 5), y: tieY - 1 }],
        'none', c.color('woodBase'), 2, { 'data-part': 'reed-lashing' }, false);
    }
    ties += c.path(`M${n(tx - 6)} ${n(tieY - 3)}l12 -2 1 7 -12 2Z`, [{ x: tx - 6, y: tieY - 5 }, { x: tx + 7, y: tieY + 4 }],
      c.color('clothBlue'), c.color('outlineSoft'), 0.6, { 'data-part': 'reed-tie' }, false);
  }

  const layers = [
    { id: 'back-stalks', renderRole: 'dressing' as const, body: back + middle },
    { id: 'ties', renderRole: 'dressing' as const, body: ties },
    { id: 'edge-stalks', renderRole: 'foreground-edge' as const, body: front },
  ];
  return c.finish(
    layers.map((l) => el('g', { 'data-layer': l.id }, l.body)).join(''),
    [
      anchor('gap_center', 0, -h * 0.45), anchor('gap_left', -gap / 2, -h * 0.45), anchor('gap_right', gap / 2, -h * 0.45),
      anchor('tie_left', -tieOffset, tieY), anchor('tie_right', tieOffset, tieY),
      anchor('base_left', -w / 2, 0, 'support'), anchor('base_right', w / 2, 0, 'support'),
    ],
    [{ name: 'curtain_gap', role: 'opening', bounds: { x: -gap / 2, y: -h * 0.8, width: gap, height: h * 0.8 }, gameplayAuthority: false }],
    { kind: 'bottom-center', point: { x: 0, y: 0 } },
    { stalks, opening: p.opening, gapWidth: gap },
    layers,
  );
}
